import axios from 'axios';
import api from './api';

/**
 * Enhanced Paywall Service - API Integration
 * Handles paywall operations with credit system integration
 */

/**
 * Custom error class for insufficient credits.
 * This extends Error, providing proper stack traces and error handling.
 */
class InsufficientCreditsError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'InsufficientCreditsError';
    this.type = 'insufficient_credits';
    this.requiredCredits = details.required_credits;
    this.currentCredits = details.current_credits;
    this.rechargeNeeded = details.recharge_needed;
    // Keep a reference to the original error response if useful for debugging
    this.originalErrorResponse = details.originalErrorResponse;
  }
}

/**
 * Check access to content with credit information
 * @param {string} contentType - Type of content ('page', 'album', etc.)
 * @param {number} contentId - ID of the content
 * @returns {Promise<Object>} Access result with credit information
 */
export const checkAccess = async (contentType, contentId) => {
  try {
    const response = await api.post('/paywall/check-access', {
      content_type: contentType,
      content_id: contentId
    });

    // Return the actual API response structure
    return response.data;
  } catch (error) {
    console.error('Error checking access:', error);

    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to check access');
    } else if (axios.isAxiosError(error) && error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to check access');
    }
  }
};

/**
 * Purchase content with credits
 * @param {string} contentType - Type of content ('page', 'album', etc.)
 * @param {number} contentId - ID of the content
 * @returns {Promise<Object>} Purchase result
 */
export const purchaseContent = async (contentType, contentId) => {
  try {
    const response = await api.post('/paywall/purchase', {
      content_type: contentType,
      content_id: contentId
    });

    // Return the actual API response structure
    return response.data;
  } catch (error) {
    console.error('Error purchasing content:', error);

    // Handle insufficient credits (HTTP 402)
    if (axios.isAxiosError(error) && error.response?.status === 402) {
      const errorData = error.response.data;
      throw new InsufficientCreditsError(
          errorData.detail || errorData.message || 'Insufficient credits',
          {
            required_credits: errorData.required_credits || errorData.cost_in_credits,
            current_credits: errorData.current_credits || errorData.user_credit_balance,
            recharge_needed: errorData.recharge_needed || errorData.credits_needed,
            originalErrorResponse: error.response // Pass the full response for more context
          }
      );
    }

    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to purchase content');
    } else if (axios.isAxiosError(error) && error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to purchase content');
    }
  }
};

/**
 * Get detailed credit status for content
 * @param {string} contentType - Type of content ('page', 'album', etc.)
 * @param {number} contentId - ID of the content
 * @returns {Promise<Object>} Detailed credit status
 */
export const getCreditStatus = async (contentType, contentId) => {
  try {
    const response = await api.get(`/paywall/credit-status/${contentType}/${contentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching credit status:', error);

    if (axios.isAxiosError(error) && error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to fetch credit status');
    } else if (axios.isAxiosError(error) && error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to fetch credit status');
    }
  }
};

/**
 * Check if user has sufficient credits for content
 * @param {string} contentType - Type of content
 * @param {number} contentId - ID of the content
 * @returns {Promise<boolean>} Whether user has sufficient credits
 */
export const hasSufficientCredits = async (contentType, contentId) => {
  try {
    const accessResult = await checkAccess(contentType, contentId);

    // Handle the actual API response structure
    return accessResult.has_access || !accessResult.insufficient_credits;
  } catch (error) {
    console.error('Error checking sufficient credits:', error);
    // If checkAccess throws, it means there was an error preventing the check,
    // so it's safer to assume no access or insufficient credits.
    return false;
  }
};

/**
 * Get recharge recommendation for content
 * @param {string} contentType - Type of content
 * @param {number} contentId - ID of the content
 * @returns {Promise<Object>} Recharge recommendation
 */
export const getRechargeRecommendation = async (contentType, contentId) => {
  try {
    const accessResult = await checkAccess(contentType, contentId);

    return {
      required_credits: accessResult.cost_in_credits || 0,
      current_credits: accessResult.user_credit_balance || 0,
      recharge_needed: accessResult.credits_needed || 0,
      recommended_amount: accessResult.credits_needed || 25 // Default to 25 if not provided
    };
  } catch (error) {
    console.error('Error getting recharge recommendation:', error);
    // If checkAccess fails, rethrow a general error. The specific error handling for checkAccess
    // already happens within checkAccess itself.
    throw new Error('Failed to get recharge recommendation: ' + error.message);
  }
};

// Export all functions as default object for easier importing
const paywallService = {
  checkAccess,
  purchaseContent,
  getCreditStatus,
  hasSufficientCredits,
  getRechargeRecommendation
};

export default paywallService;